/**
 * Runtime configuration for partner-embedded (iframe) mode.
 *
 * When the app is loaded inside a partner site's iframe, the widget loader
 * forwards configuration via query params:
 *
 *   ?embed=1&key=<partner APIM subscription key>&theme=<theme>&locale=<locale>
 *     &source_url=<partner page the widget is running on>
 *
 * In this mode the app runs guest-only and routes all backend traffic through
 * the partners APIM product using the per-partner key (instead of the baked-in
 * first-party key). The values are snapshotted once at module load so later
 * client-side navigation cannot change them.
 */

const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');

const embedFlag = params.get('embed') === '1' || params.get('embed') === 'true';
const embedKey = params.get('key')?.trim() || undefined;
const embedTheme = params.get('theme')?.trim() || undefined;
const embedLocale = params.get('locale')?.trim() || undefined;
const embedSourceUrl = ((): string | undefined => {
  // Gated on embed mode so a first-party session that happens to carry a
  // `source_url` query param (e.g. a shared link) never picks it up — only
  // the widget loader's iframe URL sets both `embed=1` and `source_url`.
  if (!embedFlag) return undefined;
  const raw = params.get('source_url')?.trim();
  if (!raw || raw.length > 2048) return undefined;
  // Only http(s) is forwarded; the backend re-validates and additionally
  // requires the host to be one of the partner's allowed domains.
  return /^https?:\/\//i.test(raw) ? raw : undefined;
})();

/** True when the app is running inside a partner embed iframe. */
export const isEmbed = (): boolean => embedFlag;

/** Partner APIM subscription key forwarded by the loader, if any. */
export const getEmbedApimKey = (): string | undefined => embedKey;

/** Theme requested by the loader (e.g. 'light' | 'dark'), if any. */
export const getEmbedTheme = (): string | undefined => embedTheme;

/**
 * UI locale requested by the loader, if any — one of the 16 codes in
 * `constants/languages.ts`. Forwarded verbatim from the partner's `data-locale`,
 * so expect any casing and region tags (`zh-TW`, `pt-BR`); `i18n.ts` resolves it
 * and falls back to English.
 */
export const getEmbedLocale = (): string | undefined => embedLocale;

/**
 * URL of the partner page hosting the widget, if the loader reported one.
 *
 * Sent with each chat turn so the backend can make that page the first source
 * for answers — a visitor asking "who is running?" on a Virginia 2026 elections
 * page should get an answer grounded in that page.
 */
export const getEmbedSourceUrl = (): string | undefined => embedSourceUrl;
